import { useMemo } from 'react';

import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { RoomType } from '@/models/Room.ts';
import { SelectionOption } from '@/models/SelectionOption.ts';

interface FiltersHook {
    room_options: SelectionOption[];
    practitioner_options: SelectionOption[];
    calendar_options: SelectionOption[];
    practice_options: SelectionOption[];
}

const useFilters = (): FiltersHook => {
    const { configs, location_key } = useConfig();
    const { data, calendars } = useEnrichedPractitionerData();

    // Create room options from selected location
    const room_options = useMemo(() => {
        return (configs ?? []).flatMap(
            (config) =>
                config.rooms
                    ?.filter(({ room_type }) => room_type === RoomType.ROOM)
                    .map(({ room_name, room_id }) => ({
                        label: room_name,
                        value: room_id,
                    })) ?? []
        );
    }, [configs]);

    // Create practitioner options from data, filtered by current location
    const practitioner_options = useMemo(() => {
        return (
            data?.flatMap((instance) =>
                instance.practitioners
                    ?.filter((prac) => prac.practices.some((practice) => practice.prismic_hub.key === location_key))
                    .map((prac) => ({
                        label: prac.name,
                        value: prac.id,
                    }))
            ) ?? []
        );
    }, [data, location_key]);

    // Create calendar options from data, filtered by current location
    const calendar_options: SelectionOption[] = useMemo(() => {
        const location_practitioner_ids =
            data?.flatMap((instance) =>
                instance.practitioners
                    .filter((prac) => prac.practices.some((practice) => practice.prismic_hub.key === location_key))
                    .map((prac) => prac.id)
            ) ?? [];

        if (location_practitioner_ids.length === 0) return [];

        return calendars
            .filter((calendar) => {
                // Check if this calendar belongs to a practitioner at this location
                return data?.some((instance) =>
                    instance.practitioners.some(
                        (prac) =>
                            location_practitioner_ids.includes(prac.id) &&
                            prac.calendars.some((cal) => cal.pvs.id === calendar.id)
                    )
                );
            })
            .map((calendar) => ({
                label: calendar.name,
                value: calendar.id,
            }));
    }, [calendars, data, location_key]);

    const practice_options = useMemo(() => {
        if (!data) return [];

        return data.flatMap(
            (instance_data) =>
                instance_data?.practices?.map((practice) => ({
                    label: practice.pvs?.name ?? '',
                    value: practice.pvs?.id ?? '',
                })) ?? []
        );
    }, [data]);

    return {
        room_options,
        practitioner_options,
        calendar_options,
        practice_options,
    };
};

export default useFilters;
